import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, TextField, Button, Typography,
  Alert, CircularProgress, InputAdornment, IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { AxiosError } from 'axios';
import { useAuth } from '../shared/contexts/AuthContext';
import { DEFAULT_CATALOGUE_SETTINGS } from '../shared/types';
import { API_BASE_URL } from '../shared/services/apiClient';

export const ChangePasswordPage: React.FC = () => {
  const { changePassword, mustChangePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const settings = DEFAULT_CATALOGUE_SETTINGS;
  const logoUrl = `${API_BASE_URL}/api/branding/logo/${encodeURIComponent(settings.logo_file)}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (next.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (next !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await changePassword(current, next);
      navigate('/');
    } catch (err) {
      const msg =
        err instanceof AxiosError
          ? (err.response?.data as { detail?: string })?.detail ?? err.message
          : 'Could not change password.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F7F6F2', p: 2 }}>
      <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 400, borderRadius: 2 }} component="form" onSubmit={handleSubmit}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            component="img"
            src={logoUrl}
            alt="Brand logo"
            sx={{ height: 64, objectFit: 'contain' }}
            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        </Box>

        <Typography variant="h6" align="center" gutterBottom sx={{ color: '#363D4C', fontWeight: 700 }}>
          {mustChangePassword ? 'Set a new password' : 'Change password'}
        </Typography>
        <Typography variant="body2" align="center" sx={{ color: '#777', mb: 3 }}>
          {mustChangePassword
            ? 'Please choose a new password before continuing.'
            : 'Update the password for your catalogue account.'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          label="Current password"
          type={show ? 'text' : 'password'}
          value={current}
          onChange={e => setCurrent(e.target.value)}
          fullWidth
          autoFocus
          autoComplete="current-password"
          sx={{ mb: 2 }}
          disabled={loading}
        />
        <TextField
          label="New password"
          type={show ? 'text' : 'password'}
          value={next}
          onChange={e => setNext(e.target.value)}
          fullWidth
          autoComplete="new-password"
          sx={{ mb: 2 }}
          disabled={loading}
          helperText="At least 8 characters"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShow(v => !v)} edge="end" size="small">
                  {show ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Confirm new password"
          type={show ? 'text' : 'password'}
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          fullWidth
          autoComplete="new-password"
          sx={{ mb: 3 }}
          disabled={loading}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={loading || !current || !next || !confirm}
          sx={{ bgcolor: '#A55DA0', '&:hover': { bgcolor: '#8d4a88' }, fontWeight: 700, mb: 1 }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Save password'}
        </Button>

        <Button fullWidth onClick={logout} disabled={loading} sx={{ color: '#777', textTransform: 'none' }}>
          Sign out
        </Button>
      </Paper>
    </Box>
  );
};

export default ChangePasswordPage;
